import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { css, Label, Modal } from './UI';
import { validarCLABE } from '../utils';

// Varias cuentas bancarias por proveedor (backend: utils/cuentasProveedor.js).
// El catálogo las da de alta (admin y finanzas), el solicitante elige una al
// capturar o pide que la confirme Cuentas por Pagar, y programación la confirma.

export const POR_CONFIRMAR = '__por_confirmar__';
const digitos = (v) => String(v || '').replace(/\D/g, '');
export const claveCuenta = (c) => digitos(c.clabe) || digitos(c.cuenta);
export const etiquetaCuentaProveedor = (c) => {
  const fin = claveCuenta(c).slice(-4);
  return [c.banco, fin && `••${fin}`].filter(Boolean).join(' ') || 'Cuenta';
};
export const cuentaVacia = (principal = false) => ({ banco: '', clabe: '', cuenta: '', principal, activa: true });

// Cuentas del proveedor; los registros anteriores solo traen banco/clabe/cuenta sueltos
export function cuentasDelProveedor(p, conInactivas = false) {
  if (!p) return [];
  const cs = p.cuentas?.length
    ? p.cuentas
    : (p.clabe || p.cuenta ? [{ banco: p.banco || '', clabe: p.clabe || '', cuenta: p.cuenta || '', principal: true, activa: true }] : []);
  return conInactivas ? cs.map(c => ({ ...c })) : cs.filter(c => c.activa !== false);
}

const fechaCorta = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

// ── Catálogo: editor de las cuentas de un proveedor ─────────────────────────
export function CuentasProveedorEditor({ cuentas = [], onChange }) {
  const cambiar = (i, k, v) => onChange(cuentas.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const hacerPrincipal = (i) => onChange(cuentas.map((c, j) => ({ ...c, principal: j === i })));
  const quitar = (i) => onChange(cuentas.filter((_, j) => j !== i));
  // Una cuenta que ya se guardó no se borra: se desactiva y queda quién y cuándo
  const alternarActiva = (i) => onChange(cuentas.map((c, j) => (j === i
    ? { ...c, activa: c.activa === false, principal: c.activa === false ? c.principal : false }
    : c)));

  return (
    <div>
      <Label>Cuentas bancarias</Label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {cuentas.map((c, i) => {
          const inactiva = c.activa === false;
          const r = validarCLABE(c.clabe);
          return (
            <div key={c._id || i} style={{ padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.principal ? 'var(--plasma)' : 'var(--hair)'}`, background: inactiva ? 'var(--hair-soft)' : 'var(--panel)', opacity: inactiva ? 0.6 : 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr 1.2fr', gap: 8 }}>
                <input value={c.banco} disabled={inactiva} onChange={e => cambiar(i, 'banco', e.target.value)} placeholder="Banco" style={css.input(false)} />
                <input value={c.clabe} disabled={inactiva} onChange={e => cambiar(i, 'clabe', e.target.value)} placeholder="CLABE (18 dígitos)" style={{ ...css.input(false), fontFamily: 'Geist Mono, monospace' }} />
                <input value={c.cuenta} disabled={inactiva} onChange={e => cambiar(i, 'cuenta', e.target.value)} placeholder="No. de cuenta" style={{ ...css.input(false), fontFamily: 'Geist Mono, monospace' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 7, fontSize: 11.5, flexWrap: 'wrap' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: inactiva ? 'default' : 'pointer', color: 'var(--ink-soft)' }}>
                  <input type="radio" name="cuentaPrincipal" checked={!!c.principal} disabled={inactiva} onChange={() => hacerPrincipal(i)} />
                  Principal
                </label>
                {r && !inactiva && <span style={{ fontWeight: 600, color: r.valida ? 'var(--ok)' : 'var(--danger)' }}>{r.valida ? '✓' : '✗'} {r.mensaje}</span>}
                <span style={{ color: 'var(--glass)' }}>
                  {inactiva && c.baja ? `Desactivada ${fechaCorta(c.baja)}${c.bajaPor ? ` por ${c.bajaPor}` : ''}`
                    : c.alta ? `Alta ${fechaCorta(c.alta)}${c.altaPor ? ` por ${c.altaPor}` : ''}` : 'Nueva'}
                </span>
                <span style={{ flex: 1 }} />
                {c._id || c.alta
                  ? <button type="button" onClick={() => alternarActiva(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 600, color: inactiva ? 'var(--ok)' : 'var(--danger)' }}>{inactiva ? 'Reactivar' : 'Desactivar'}</button>
                  : <button type="button" onClick={() => quitar(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 600, color: 'var(--danger)' }}>Quitar</button>}
              </div>
            </div>
          );
        })}
      </div>
      <button type="button" onClick={() => onChange([...cuentas, cuentaVacia(!cuentas.some(c => c.activa !== false))])}
        style={{ marginTop: 8, padding: '5px 12px', borderRadius: 6, border: '1px dashed var(--hair-strong)', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--ink-soft)' }}>
        + Agregar cuenta
      </button>
      <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 4 }}>
        Si tiene varias, el solicitante elige a cuál se paga o pide que la confirme Cuentas por Pagar.
      </div>
    </div>
  );
}

// ── Captura: a qué cuenta del proveedor se paga ─────────────────────────────
// value: clave de la cuenta elegida, o POR_CONFIRMAR.
export function SelectorCuentaProveedor({ cuentas, value, onChange, error }) {
  if (!cuentas || cuentas.length < 2) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <Label required>¿A qué cuenta se paga?</Label>
      <select value={value || ''} onChange={e => onChange(e.target.value)} style={css.select(error)}>
        <option value="">Elige la cuenta…</option>
        {cuentas.map(c => (
          <option key={claveCuenta(c)} value={claveCuenta(c)}>
            {etiquetaCuentaProveedor(c)}{c.principal ? ' · principal' : ''}
          </option>
        ))}
        <option value={POR_CONFIRMAR}>No sé, que la confirme Cuentas por Pagar</option>
      </select>
      {value === POR_CONFIRMAR && (
        <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 3 }}>
          Cuentas por Pagar elegirá la cuenta antes de programar el pago.
        </div>
      )}
      {error && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 3 }}>Elige la cuenta o marca que la confirme Cuentas por Pagar</div>}
    </div>
  );
}

// ── Cuentas por Pagar: confirma la cuenta antes de programar o pagar ────────
// onConfirmada recibe los datos nuevos de la solicitud (banco, clabe, cuenta…).
export function ModalConfirmarCuenta({ solicitud, onClose, onConfirmada }) {
  const [datos, setDatos] = useState(null);
  const [eleccion, setEleccion] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const id = solicitud?._id;

  useEffect(() => {
    if (!id) return;
    let vivo = true;
    setDatos(null); setEleccion(''); setError('');
    api.get(`/solicitudes/${id}/cuentas-proveedor`)
      .then(d => { if (vivo) setDatos(d); })
      .catch(e => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [id]);

  const confirmar = async () => {
    if (!eleccion) return;
    setGuardando(true); setError('');
    try {
      const r = await api.patch(`/solicitudes/${id}/cuenta-proveedor`, { cuenta: eleccion });
      onConfirmada({ ...r, cuentaPorConfirmar: false });
    } catch (e) { setError(e.message); }
    finally { setGuardando(false); }
  };

  return (
    <Modal open={!!solicitud} onClose={onClose} title="🏦 Confirmar cuenta del proveedor" width={500}>
      {solicitud && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
          <div style={{ fontSize: 12.5, color: 'var(--glass)' }}>
            Quien capturó <b style={{ color: 'var(--ink)' }}>{solicitud.folio}</b> no supo a cuál cuenta de{' '}
            <b style={{ color: 'var(--ink)' }}>{solicitud.proveedor}</b> se paga. Elígela para poder programar el pago.
          </div>
          {!datos && !error && <div style={{ color: 'var(--glass)' }}>Cargando cuentas…</div>}
          {datos && !datos.cuentas.length && (
            <div style={{ color: 'var(--danger)' }}>El proveedor ya no tiene cuentas activas. Pide a Admin o Finanzas que las den de alta en el catálogo.</div>
          )}
          {datos && datos.cuentas.map(c => (
            <label key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
              border: `1px solid ${eleccion === c.id ? 'var(--plasma)' : 'var(--hair)'}`,
              background: eleccion === c.id ? 'rgba(255,84,28,0.08)' : 'var(--panel)',
            }}>
              <input type="radio" name="cuentaProveedor" checked={eleccion === c.id} onChange={() => setEleccion(c.id)} />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                <span style={{ fontWeight: 600 }}>{c.banco || 'Cuenta'}{c.principal ? ' · principal' : ''}</span>
                <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11.5, color: 'var(--glass)' }}>
                  {c.clabe ? `CLABE ${c.clabe}` : `Cuenta ${c.cuenta}`}
                </span>
              </span>
              {c.nueva && <span title="Nunca ha recibido un pago o se dio de alta hace poco: verifícala con el proveedor" style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>⚠️ Cuenta nueva</span>}
            </label>
          ))}
          {error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ ...css.btn('#6b7280', true), fontSize: 13 }}>Cancelar</button>
            <button onClick={confirmar} disabled={!eleccion || guardando} style={{ ...css.btn('#00b8c4'), fontSize: 13, opacity: !eleccion || guardando ? 0.6 : 1 }}>
              {guardando ? 'Guardando…' : 'Confirmar cuenta'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// Aviso para listas y detalles: cuenta por confirmar o cuenta nueva
export function AvisoCuentaProveedor({ s, compacto = false }) {
  if (!s) return null;
  if (s.cuentaPorConfirmar) return (
    <span title="El solicitante no supo a qué cuenta se paga: Cuentas por Pagar la confirma antes de programar"
      style={{ fontSize: compacto ? 10.5 : 11.5, fontWeight: 700, color: '#b45309', whiteSpace: 'nowrap' }}>🏦 Cuenta por confirmar</span>
  );
  if (s.cuentaNueva && !['pagada', 'rechazada'].includes(s.estado)) return (
    <span title="Esta cuenta nunca ha recibido un pago o se dio de alta hace poco: verifícala antes de pagar"
      style={{ fontSize: compacto ? 10.5 : 11.5, fontWeight: 700, color: '#b45309', whiteSpace: 'nowrap' }}>⚠️ Cuenta nueva</span>
  );
  return null;
}
